'use client'

import { useMemo } from 'react'

import { track } from '@amplitude/analytics-browser'

import { buildDepartUrl } from '@/lib/c2c/build-depart-url'

import { writeSharedSelection } from './shared-search-store'
import { useSearchTracking, type SearchSubmitErrors } from './use-search-tracking'
import type { WidgetState } from './use-widget-state'

export type SubmitOutcome =
    | { kind: 'valid'; url: string }
    | { kind: 'invalid'; errors: SearchSubmitErrors }

export type CollapsedSubmitOutcome = { kind: 'valid'; url: string } | { kind: 'expand' }

export interface SearchSubmitter {
    /** Called from `SearchWidget` surfaces (hero and sticky-bar-embedded). */
    submit: () => SubmitOutcome
    /**
     * Called from the mobile-collapsed sticky-bar search button. Invalid input
     * returns `{ kind: 'expand' }` and fires neither tracker.
     */
    submitFromCollapsed: () => CollapsedSubmitOutcome
}

/**
 * Shared submit handler for the c2c `SearchWidget` and the mobile-collapsed
 * `StickySearchBar` search button. Computes validity, fires Snowplow and
 * (on valid) Amplitude events with identical property shape across all
 * surfaces.
 */
export function useSearchSubmit(state: WidgetState, locale: string): SearchSubmitter {
    const tracking = useSearchTracking()

    return useMemo<SearchSubmitter>(() => {
        function computeErrors(): SearchSubmitErrors {
            const errors: SearchSubmitErrors = {
                from: !state.from || !state.fromId,
                to: !state.to || !state.toId,
                departDate: !state.departDate,
            }
            if (state.fromId && state.toId && state.fromId === state.toId) {
                errors.from = true
                errors.to = true
            }
            return errors
        }

        function buildSubmitInputs() {
            return {
                departDate: state.departDate,
                destination: state.to,
                destinationPlaceId: state.toId,
                origin: state.from,
                originPlaceId: state.fromId,
                returnDate: state.returnDate ?? '',
            }
        }

        function fireValidEvents(): string {
            const submitInputs = buildSubmitInputs()
            tracking.trackSubmit(submitInputs, {})

            // Publish the submitted search for the legacy booking application. Submission
            // is the trigger because it is the point at which the selection is
            // unambiguously the visitor's, and already validated; every presentation
            // reaches the store through here.
            writeSharedSelection(state)
            track('Search Submitted', {
                arrive_address: state.to,
                arrive_place_id: state.toId,
                depart_address: state.from,
                depart_place_id: state.fromId,
                is_one_way: state.tripType === 'one-way',
                outbound_date_searched: state.departDate,
                pax_count: state.passengers,
                return_date_searched:
                    state.tripType === 'round-trip' && state.returnDate
                        ? state.returnDate
                        : undefined,
            })

            const query: Record<string, string> = {}
            if (state.fromId) {
                query.opid = state.fromId
            }
            if (state.toId) {
                query.dpid = state.toId
            }
            // Seat count for the booking application's `p` search param, always present.
            query.p = String(state.passengers)

            return buildDepartUrl({
                origin: state.from,
                destination: state.to,
                date: state.departDate,
                returnDate:
                    state.tripType === 'round-trip' && state.returnDate
                        ? state.returnDate
                        : undefined,
                locale,
                query,
            })
        }

        return {
            submit(): SubmitOutcome {
                const errors = computeErrors()
                if (errors.from || errors.to || errors.departDate) {
                    tracking.trackSubmit(buildSubmitInputs(), errors)
                    return { kind: 'invalid', errors }
                }
                return { kind: 'valid', url: fireValidEvents() }
            },
            submitFromCollapsed(): CollapsedSubmitOutcome {
                const errors = computeErrors()
                if (errors.from || errors.to || errors.departDate) {
                    return { kind: 'expand' }
                }
                return { kind: 'valid', url: fireValidEvents() }
            },
        }
    }, [state, locale, tracking])
}
